import { useMemo, useContext } from 'react';
import { useFormikContext } from 'formik';
import { Box } from '@mui/material';

import { SupportDocsUpload } from '../DocumentsUpload';

import useLocale from '../../app/hooks/useLocale';

import { EditExpenseContext } from './EditExpenseProvider';
import { IFormValues } from './types';
import { isExpenseFilled } from './utils';
import { ClientPublicService } from '../../app/api/ClientPublicService';

const EditExpenseDocumentsTab = ({ ...props }: any) => {
  const { t } = useLocale();
  const { values } = useFormikContext<IFormValues>();
  const { isEditable } = useContext(EditExpenseContext);

  const isChildSupport = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.ChildSupportPayments, values),
    [values]
  );

  const isSpousalSupport = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.SpousalSupportPayments, values),
    [values]
  );

  const isChildCare = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.ChildCare, values),
    [values]
  );

  const isMedicalOrDentalOrPrescription = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.MedicalOrDentalOrPrescription, values),
    [values]
  );

  const isFinesOrPenaltiesImposedByCourt = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.FinesOrPenaltiesImposedByCourt, values),
    [values]
  );

  const isAsAConditionOfEmployment = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.AsAConditionOfEmployment, values),
    [values]
  );

  const isDebtsWhereStayHasBeenLifted = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.DebtsWhereStayHasBeenLifted, values),
    [values]
  );

  const isOther_NonDiscretionary = useMemo(
    () => isExpenseFilled(ClientPublicService.ExpenseDescriptionEnum.Other_NonDiscretionary, values),
    [values]
  );

  const documentPrefixCodes = useMemo(() => {
    const codes = [];

    if (isChildSupport) {
      codes.push('Expense.ChildSupportPayments');
    }

    if (isSpousalSupport) {
      codes.push('Expense.SpousalSupportPayments');
    }

    if (isChildCare) {
      codes.push('Expense.ChildCare');
    }

    if (isMedicalOrDentalOrPrescription) {
      codes.push('Expense.MedicalOrDentalOrPrescription');
    }

    if (isFinesOrPenaltiesImposedByCourt) {
      codes.push('Expense.FinesOrPenaltiesImposedByCourt');
    }

    if (isAsAConditionOfEmployment) {
      codes.push('Expense.AsAConditionOfEmployment');
    }

    if (isDebtsWhereStayHasBeenLifted) {
      codes.push('Expense.DebtsWhereStayHasBeenLifted');
    }

    if (isOther_NonDiscretionary) {
      codes.push('Expense.Other_NonDiscretionary');
    }

    return codes;
  }, [isChildSupport, isSpousalSupport]);

  const tooltip = useMemo(() => {
    const tooltipArray = [];

    if (isChildSupport) {
      tooltipArray.push(t.ONLY_1_OF_THE_2_SUPPORTING_DOCUMENTS_LISTED_FOR_CHILD_SUPPORT_PAYMENT_IS_REQUIRED);
    }

    if (isSpousalSupport) {
      tooltipArray.push(t.ONLY_1_OF_THE_2_SUPPORTING_DOCUMENTS_LISTED_FOR_SPOUSAL_SUPPORT_PAYMENT_IS_REQUIRED);
    }

    return tooltipArray?.length ? tooltipArray : undefined;
  }, [
    isChildSupport,
    isSpousalSupport,
    t.ONLY_1_OF_THE_2_SUPPORTING_DOCUMENTS_LISTED_FOR_CHILD_SUPPORT_PAYMENT_IS_REQUIRED,
    t.ONLY_1_OF_THE_2_SUPPORTING_DOCUMENTS_LISTED_FOR_SPOUSAL_SUPPORT_PAYMENT_IS_REQUIRED,
  ]);

  return (
    <Box data-testid="expense-documents">
      <SupportDocsUpload
        {...props}
        tooltip={tooltip}
        documentFolderCode="surplus-ie"
        documentPrefixCodes={documentPrefixCodes}
        disabled={!isEditable}
      />
    </Box>
  );
};

export default EditExpenseDocumentsTab;
